import { ref, watch, type Ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type {
  FetchTicketResponse,
  FetchTicketResponsePriority,
  NestedTicketStatus,
  NestedTicketStatusDetail,
} from 'src/api/models';
import type { AssigneeData } from 'src/services/assignee-service';
import { useTicketService } from 'src/services/ticket-service';
import { useNotification } from 'src/composables/useNotification';
import RequestCancellation from 'src/api/request-cancellation';
import { toUTCDateTimeString, parseUTCDate } from 'src/utils/date-utc';

export interface UseTicketEditOptions {
  ticketId: Ref<string>;
  onTicketLoaded?: (ticket: FetchTicketResponse) => void;
}

export function useTicketEdit(options: UseTicketEditOptions) {
  const { ticketId, onTicketLoaded } = options;

  const ticket = ref<FetchTicketResponse | null>(null);
  const isLoading = ref(false);
  const isInitialLoad = ref(true);

  const { showError } = useNotification();
  const ticketService = useTicketService();


  async function loadTicket(): Promise<void> {
    if (!ticketId.value) return;

    isLoading.value = true;
    isInitialLoad.value = true;

    try {
      ticket.value = await ticketService.fetchTicket(ticketId.value);
      onTicketLoaded?.(ticket.value);
    } catch {
      showError({ message: 'Failed to load ticket' });
      ticket.value = null;
    } finally {
      isLoading.value = false;
      isInitialLoad.value = false;
    }
  }

  // Each method: update local state optimistically, then persist.

  async function updateTitle(title: string): Promise<void> {
    if (!ticket.value || isInitialLoad.value) return;
    ticket.value.title = title;
    await safeUpdate(() => ticketService.updateTicketTitle(ticketId.value, title));
  }

  const updateDescription = useDebounceFn((description: string) => {
    if (!ticket.value) return;
    ticket.value.description = description;
    safeUpdate(() => ticketService.updateTicketDescription(ticketId.value, description));
  }, 500);

  async function updatePriority(priority: FetchTicketResponsePriority): Promise<void> {
    if (!ticket.value) return;
    ticket.value.priority = priority;
    await safeUpdate(() => ticketService.updateTicketPriority(ticketId.value, priority));
  }

  async function updateStatus(status: NestedTicketStatus | null): Promise<void> {
    if (!ticket.value || !status?.id) return;
    ticket.value.status = status;
    ticket.value.status_detail = undefined;
    await safeUpdate(() =>
      ticketService.updateTicketStatus(ticketId.value, status.id!),
    );
  }

  async function updateStatusDetail(detail: NestedTicketStatusDetail | null): Promise<void> {
    if (!ticket.value || !ticket.value.status?.id) return;
    ticket.value.status_detail = detail ?? undefined;
    await safeUpdate(() =>
      ticketService.updateTicketStatus(ticketId.value, ticket.value!.status!.id!, detail?.id),
    );
  }

  async function updateScheduledDate(date: Date | null): Promise<void> {
    if (!ticket.value) return;
    ticket.value.scheduled_date = date ? toUTCDateTimeString(date) : null;
    await safeUpdate(() =>
      ticketService.updateTicketPlannedDate(ticketId.value, date ? toUTCDateTimeString(date) : ''),
    );
  }

  async function updateExpectedDuration(duration: { hours: number; minutes: number }): Promise<void> {
    if (!ticket.value) return;
    ticket.value.expected_duration = duration as any;
    await safeUpdate(() => ticketService.updateTicketExpectedDuration(ticketId.value, duration));
  }

  async function updateAssignee(assignee: AssigneeData | null): Promise<void> {
    if (!ticket.value) return;
    ticket.value.assignee = assignee as any;
    await safeUpdate(() =>
      ticketService.updateTicketAssignee(ticketId.value, assignee?.id ?? null),
    );
  }


  function getScheduledDate(): Date | undefined {
    return ticket.value?.scheduled_date ? parseUTCDate(ticket.value.scheduled_date) : undefined;
  }

  function getExpectedDuration(): { hours: number; minutes: number } | undefined {
    const d = ticket.value?.expected_duration;
    if (!d) return undefined;
    if (typeof d === 'object' && 'hours' in d) return d as any;
    return undefined;
  }


  function reset(): void {
    ticket.value = null;
    isLoading.value = false;
    isInitialLoad.value = true;
    RequestCancellation.cancelQueries('Ticket edit reset');
  }

  watch(ticketId, async (newId, oldId) => {
    if (newId && newId !== oldId) {
      RequestCancellation.cancelQueries('Ticket changed');
      await loadTicket();
    }
  });


  async function safeUpdate(fn: () => Promise<unknown>): Promise<void> {
    try {
      await fn();
    } catch (error) {
      console.error('Ticket update failed:', error);
    }
  }

  return {
    ticket,
    isLoading,
    isInitialLoad,
    loadTicket,
    reset,
    getScheduledDate,
    getExpectedDuration,
    updateTitle,
    updateDescription,
    updatePriority,
    updateStatus,
    updateStatusDetail,
    updateAssignee,
    updateScheduledDate,
    updateExpectedDuration,
  };
}
